import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';

const { FiEdit2, FiTrash2, FiChevronUp, FiChevronDown, FiEye, FiEyeOff, FiPlayCircle, FiCheckCircle, FiCheck } = FiIcons;

function EntriesTable({ entries, sortBy, sortOrder, onSort, onUpdateEntry, onUpdateCompletion, onDeleteEntry }) {
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRowExpansion = (id) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const handleCompletionToggle = (id, isComplete) => {
    onUpdateCompletion(id, isComplete);
  };

  const SortButton = ({ field, children }) => (
    <button 
      onClick={() => onSort(field)} 
      className="flex items-center space-x-1 text-left font-medium text-gray-700 hover:text-gray-900 transition-colors"
    >
      <span>{children}</span>
      {sortBy === field && (
        <SafeIcon icon={sortOrder === 'asc' ? FiChevronUp : FiChevronDown} className="text-sm text-purple-600" />
      )}
    </button>
  );

  const getImpactColor = (value) => {
    if (value > 0) return 'text-green-600 bg-green-50';
    if (value < 0) return 'text-red-600 bg-red-50';
    return 'text-gray-500 bg-gray-100';
  };

  const getLogStatusColor = (logCount) => {
    if (logCount >= 5) return 'text-red-600 bg-red-50';
    if (logCount >= 3) return 'text-yellow-600 bg-yellow-50';
    return 'text-gray-600 bg-gray-100';
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            <th className="px-6 py-3 text-left">
              <SortButton field="taskName">Task/System</SortButton>
            </th>
            <th className="px-6 py-3 text-left">Status</th>
            <th className="px-6 py-3 text-left">
              <SortButton field="timeSaved">Time Saved</SortButton>
            </th>
            <th className="px-6 py-3 text-left">
              <SortButton field="stepsReduced">Steps Reduced</SortButton>
            </th>
            <th className="px-6 py-3 text-left">
              <SortButton field="confusionReduction">Confusion ↓</SortButton>
            </th>
            <th className="px-6 py-3 text-left">Logs</th>
            <th className="px-6 py-3 text-left">Completed</th>
            <th className="px-6 py-3 text-left">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          <AnimatePresence>
            {entries.map((entry) => {
              const hasResults = entry.timeAfter !== null && entry.timeAfter !== undefined && entry.timeAfter !== '';
              const isLoggingDisabled = entry.log_count >= 5;
              
              return (
                <React.Fragment key={entry.id}>
                  <motion.tr 
                    initial={{ opacity: 0 }} 
                    animate={{ opacity: 1 }} 
                    exit={{ opacity: 0 }}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <button 
                          onClick={() => toggleRowExpansion(entry.id)} 
                          className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          <SafeIcon icon={expandedRows.has(entry.id) ? FiEyeOff : FiEye} className="text-sm" />
                        </button>
                        <div>
                          <p className="font-medium text-gray-900">{entry.taskName}</p>
                          <p className="text-xs text-gray-500">
                            Logged: {format(new Date(entry.createdAt), 'MMM dd')}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {hasResults ? (
                        <span className="inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          <SafeIcon icon={FiPlayCircle} className="text-xs" />
                          <span>Has Results</span>
                        </span>
                      ) : (
                        <span className="inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          <SafeIcon icon={FiPlayCircle} className="text-xs" />
                          <span>In Progress</span>
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {hasResults ? (
                        <span className={`inline-flex px-2 py-1 rounded-full text-sm font-medium ${getImpactColor(entry.timeSaved)}`}>
                          {entry.timeSaved > 0 ? '+' : ''}{entry.timeSaved} min
                        </span>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {hasResults ? (
                        <span className={`inline-flex px-2 py-1 rounded-full text-sm font-medium ${getImpactColor(entry.stepsReduced)}`}>
                          {entry.stepsReduced > 0 ? '+' : ''}{entry.stepsReduced}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {hasResults ? (
                        <span className={`inline-flex px-2 py-1 rounded-full text-sm font-medium ${getImpactColor(entry.confusionReduction)}`}>
                          {entry.confusionReduction > 0 ? '+' : ''}{entry.confusionReduction}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 rounded-full text-sm font-medium ${getLogStatusColor(entry.log_count || 0)}`}>
                        {entry.log_count || 0}/5
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={entry.is_complete || false}
                          onChange={(e) => handleCompletionToggle(entry.id, e.target.checked)}
                          className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                        />
                        <span className="text-sm text-gray-600">
                          {entry.is_complete ? 'Yes' : 'No'}
                        </span>
                      </label>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <Link 
                          to={`/edit/${entry.id}`} 
                          className={`flex items-center space-x-1 px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                            isLoggingDisabled 
                              ? 'text-gray-400 cursor-not-allowed bg-gray-100' 
                              : hasResults 
                                ? 'text-gray-600 hover:bg-gray-100' 
                                : 'bg-purple-600 text-white hover:bg-purple-700'
                          }`}
                          onClick={(e) => {
                            if (isLoggingDisabled) {
                              e.preventDefault();
                            }
                          }}
                        >
                          {isLoggingDisabled ? (
                            <>
                              <SafeIcon icon={FiCheckCircle} className="text-xs" />
                              <span>Max Logs</span>
                            </>
                          ) : hasResults ? (
                            <>
                              <SafeIcon icon={FiEdit2} className="text-xs" />
                              <span>Add Log</span>
                            </>
                          ) : (
                            <>
                              <SafeIcon icon={FiCheckCircle} className="text-xs" />
                              <span>Log Results</span>
                            </>
                          )}
                        </Link>
                        <button 
                          onClick={() => onDeleteEntry(entry.id)} 
                          className="text-red-400 hover:text-red-600 transition-colors p-1" 
                          title="Delete"
                        >
                          <SafeIcon icon={FiTrash2} className="text-sm" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                  <AnimatePresence>
                    {expandedRows.has(entry.id) && (
                      <motion.tr 
                        initial={{ opacity: 0, height: 0 }} 
                        animate={{ opacity: 1, height: 'auto' }} 
                        exit={{ opacity: 0, height: 0 }}
                        className="bg-gray-50"
                      >
                        <td colSpan="8" className="px-6 py-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 text-sm">
                            <div className="bg-white p-4 rounded-lg border border-gray-100">
                              <p className="font-semibold text-gray-900 mb-2 border-b pb-1">Baseline (Before)</p>
                              <div className="space-y-1 text-gray-600">
                                <p>Time: {entry.timeBefore} min</p>
                                <p>Steps: {entry.stepsBefore}</p>
                                <p>Confusion: {entry.confusionBefore}/10</p>
                              </div>
                            </div>
                            {hasResults ? (
                              <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                                <p className="font-semibold text-blue-900 mb-2 border-b border-blue-200 pb-1">Results (After)</p>
                                <div className="space-y-1 text-blue-800">
                                  <p>Time: {entry.timeAfter} min</p>
                                  <p>Steps: {entry.stepsAfter}</p>
                                  <p>Confusion: {entry.confusionAfter}/10</p>
                                </div>
                              </div>
                            ) : (
                              <div className="bg-gray-100 p-4 rounded-lg border border-gray-200 dashed flex items-center justify-center">
                                <span className="text-gray-500 italic">No results logged yet</span>
                              </div>
                            )}
                            <div className="md:col-span-2 lg:col-span-1">
                              <p className="font-semibold text-gray-900 mb-2">Notes</p>
                              <p className="text-gray-600 bg-white p-3 rounded border border-gray-100 h-full">
                                {entry.notes || 'No notes provided'}
                              </p>
                              {isLoggingDisabled && (
                                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-xs">
                                  Maximum of 5 logs reached. No more logging allowed.
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </motion.tr>
                    )}
                  </AnimatePresence>
                </React.Fragment>
              );
            })}
          </AnimatePresence>
        </tbody>
      </table>
    </div>
  );
}

export default EntriesTable;